import { useId, useRef } from "react";
import type { CSSProperties } from "react";

interface ComboBoxInputProps {
  value: string;
  onChange: (next: string) => void;
  /** Suggestion list. Entries are de-duplicated and rendered in given order. */
  options: ReadonlyArray<string>;
  className?: string;
  style?: CSSProperties;
  placeholder?: string;
  ariaLabel?: string;
  /** When true the underlying input is `readOnly` — disables typing but keeps
   *  the picker visible for read mode. */
  readOnly?: boolean;
  /** Optional: invoked on blur when the user leaves a non-empty value that
   *  isn't already in `options` (case-insensitive). Use this to register
   *  the new value with the server (e.g. WebAdmin2's `WebSaveValuesCB`).
   *  The host typically also kicks off a cache refresh after the call so
   *  the next render surfaces the new entry in the dropdown.
   *
   *  Idempotent — if the same new value is committed twice in a row,
   *  `onAddNew` only fires once. */
  onAddNew?: (value: string) => void;
}

/** Type-ahead text input backed by a native `<datalist>`. The user can pick
 *  from `options` or type a brand-new value (the server gets whatever string
 *  ends up in the input). Use this for fields whose set of typical values is
 *  known/derivable but not strictly enumerated — e.g. Kiosk Type, Location
 *  Chain/Country/District, FootTraffic, etc.
 *
 *  We intentionally rely on the browser-native combobox UI rather than a
 *  bespoke component: it's accessible, supports keyboard nav, and matches the
 *  user's OS conventions for free. */
export function ComboBoxInput({
  value,
  onChange,
  options,
  className,
  style,
  placeholder,
  ariaLabel,
  readOnly,
  onAddNew,
}: ComboBoxInputProps) {
  /* Last value we already fired `onAddNew` for. Lets us be idempotent across
   * re-blurs without making the host track per-field state. */
  const lastAddedRef = useRef<string | null>(null);
  const listId = useId();
  /* De-dup while preserving caller's order. Empty values dropped — they'd
   * just produce a blank entry in the dropdown. */
  const seen = new Set<string>();
  const dedup: string[] = [];
  for (const o of options) {
    if (!o) continue;
    if (seen.has(o)) continue;
    seen.add(o);
    dedup.push(o);
  }
  /* Case-insensitive map → canonical casing. If the user types "uk" and
   * the list already contains "UK", we coerce to "UK" so we don't end up
   * with two entries differing only by case. */
  const canonByLower = new Map<string, string>();
  for (const o of dedup) canonByLower.set(o.toLowerCase(), o);
  const coerce = (raw: string): string => {
    const t = raw.trim();
    if (!t) return raw;
    const canon = canonByLower.get(t.toLowerCase());
    return canon ?? raw;
  };
  return (
    <>
      <input
        list={listId}
        type="text"
        className={className}
        style={style}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        /* Coerce on blur so the user can keep typing freely, but the
         * committed value matches existing casing. Datalist autocomplete
         * already routes most picks through onChange with the canonical
         * casing — this is the safety net for free-typed input.
         * After case-coalescing, if the (now canonical) value still
         * isn't a known option and we have an onAddNew hook, register
         * the new entry server-side. */
        onBlur={(e) => {
          const raw = e.target.value;
          const coalesced = coerce(raw);
          if (coalesced !== raw) onChange(coalesced);
          const trimmed = coalesced.trim();
          if (!trimmed || readOnly) return;
          const isKnown = canonByLower.has(trimmed.toLowerCase());
          if (isKnown) return;
          if (lastAddedRef.current === trimmed) return;
          lastAddedRef.current = trimmed;
          onAddNew?.(trimmed);
        }}
      />
      <datalist id={listId}>
        {dedup.map((o) => <option key={o} value={o} />)}
      </datalist>
    </>
  );
}
